import React, { useState, useMemo, useCallback } from 'react';

import {
	Grid,
	Paper,
	Typography,
	Avatar,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Button,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { UserDto, TokensDto } from '@oh-auth/dto';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import stringToHSL from '../../helpers/stringToHSL';
import { useTranslation } from 'react-i18next';
import { UserForm } from './UserForm';
import { useRecoilValue } from 'recoil';
import { tokensState } from '../../features/auth';
import { apiClientState } from '../../features/api';
import { Transition } from './Transition';

interface UserCardProps {
	user: UserDto;
	onDelete: (user_id: string) => void;
}

const XS_CARD_SIZE = 12;
const MD_CARD_SIZE = 6;

const useStyles = makeStyles<Theme, { user: UserDto }>((theme) => ({
	paper: {
		padding: theme.spacing(2),
		textAlign: 'center',
		color: theme.palette.text.secondary,
		alignItems: 'center',
	},
	content: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
	},
	avatar: {
		width: theme.spacing(7),
		height: theme.spacing(7),
		background: ({ user }) =>
			user.nick_name
				? stringToHSL(user.nick_name, 60, 60)
				: theme.palette.grey[400],
	},
	detailContainer: {
		alignSelf: 'stretch',
		paddingLeft: theme.spacing(2),
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		justifyContent: 'center',
		flex: 1,
	},
}));

export function UserCard(props: UserCardProps) {
	const { user, onDelete } = props;
	const [currentUser, setCurrectUser] = useState<UserDto>(user);

	const tokens = useRecoilValue(tokensState) as TokensDto;
	const apiClient = useRecoilValue(apiClientState);

	const classes = useStyles({ user: currentUser });
	const [editing, setEditing] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const { t } = useTranslation();

	const handleDelete = useCallback(() => {
		apiClient.deleteUser(tokens, { user_id: user.user_id }).then(() => {
			onDelete(user.user_id);
		});
	}, [apiClient, tokens, onDelete, user.user_id]);

	const handleClose = useCallback(() => {
		setCurrectUser(user);
		setEditing(false);
	}, [user]);

	return useMemo(
		() => (
			<Grid item xs={XS_CARD_SIZE} md={MD_CARD_SIZE}>
				<Paper className={classes.paper}>
					<div className={classes.content}>
						<Avatar aria-label="recipe" className={classes.avatar}>
							{currentUser.nick_name?.slice(0, 1)}
						</Avatar>
						<div className={classes.detailContainer}>
							<Typography variant="subtitle1">
								{currentUser.nick_name}
							</Typography>
							<Typography variant="caption">
								{currentUser.username}
							</Typography>
						</div>
						<IconButton
							aria-label="edit"
							onClick={() => setEditing(true)}
						>
							<EditIcon />
						</IconButton>
						<IconButton
							aria-label="delete"
							onClick={() => setDeleting(true)}
						>
							<DeleteIcon />
						</IconButton>
					</div>
				</Paper>
				<Dialog
					maxWidth="sm"
					fullWidth
					open={editing}
					TransitionComponent={Transition}
					keepMounted
					onClose={handleClose}
					aria-labelledby="edit-dialog-slide-title"
					aria-describedby="edit-dialog-slide-description"
				>
					<UserForm
						user={currentUser}
						onClose={handleClose}
						onUserUpdate={setCurrectUser}
					/>
				</Dialog>
				<Dialog
					open={deleting}
					maxWidth="xs"
					fullWidth
					TransitionComponent={Transition}
					keepMounted
					onClose={handleClose}
					aria-labelledby={`${currentUser.user_id}-delete-title`}
				>
					<DialogTitle id={`${currentUser.user_id}-delete-title`}>
						{t('userCard.deleteTitle')}
					</DialogTitle>
					<DialogContent>
						<DialogContentText>
							{t('userCard.deleteMessage')}
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button color="primary" onClick={handleDelete}>
							{t('userCard.confirm')}
						</Button>
						<Button
							color="primary"
							onClick={() => setDeleting(false)}
						>
							{t('userCard.cancel')}
						</Button>
					</DialogActions>
				</Dialog>
			</Grid>
		),
		[
			t,
			deleting,
			editing,
			currentUser,
			classes.avatar,
			classes.content,
			classes.detailContainer,
			classes.paper,
			handleDelete,
			handleClose,
		]
	);
}
